import React, { useState, useMemo, useEffect } from "react";
import "./FleetManagementView.css";
import "../../operation/battery/OperationView.css"; // 공통 스타일 import
import "../PremiumGrid.css";
import PriceCheckModal from "./PriceCheckModal";
import {
  Vehicle,
  VehicleModel,
  ObdStatus,
  Driver,
  db,
  depots,
  vehicleModels,
  statuses
} from "../../data/database";

// --- Props 타입 정의 ---
interface FleetManagementProps {
  vehicles: Vehicle[];
  setVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>;
  drivers: Driver[];
  setDrivers: React.Dispatch<React.SetStateAction<Driver[]>>;
}

type SortDirection = "ascending" | "descending";
interface SortConfig {
  key: keyof Vehicle;
  direction: SortDirection;
}

// -------- 메인 컴포넌트 --------
export default function FleetManagementView({
  vehicles,
  setVehicles,
  drivers,
  setDrivers,
}: FleetManagementProps) {
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [formVehicle, setFormVehicle] = useState<Vehicle | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingComm, setIsCheckingComm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterModel] = useState<VehicleModel | "all">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: "vehicleNumber",
    direction: "ascending",
  });

  useEffect(() => {
    setFormVehicle(selectedVehicle);
    setIsEditing(false);
  }, [selectedVehicle]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterModel, rowsPerPage]);

  const filteredVehicles = useMemo(() => {
    let filtered = [...vehicles];

    if (searchQuery.trim()) {
      filtered = filtered.filter((v) =>
        v.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (filterModel !== "all") {
      filtered = filtered.filter((v) => v.model === filterModel);
    }

    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        const valA = (a[sortConfig.key] as any) ?? "";
        const valB = (b[sortConfig.key] as any) ?? "";
        if (valA < valB) return sortConfig.direction === "ascending" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [vehicles, sortConfig, searchQuery, filterModel]);

  // 선택된 지점에 따라 운전자 목록 필터링
  const availableDrivers = useMemo(() => {
    if (!formVehicle?.depot) return [];
    return drivers.filter((d) => d.depot === formVehicle.depot);
  }, [formVehicle?.depot, drivers]);

  // 지점이 변경되었을 때, 선택된 운전자가 해당 지점 소속이 아니면 운전자 선택을 초기화
  useEffect(() => {
    if (formVehicle && formVehicle.assignee) {
      const isDriverInDepot = availableDrivers.some(
        (d) => d.name === formVehicle.assignee
      );
      if (!isDriverInDepot) {
        // handleFormChange 대신 직접 setFormVehicle 사용 → dependency 경고 제거
        setFormVehicle((prev) =>
          prev ? { ...prev, assignee: "" } : prev
        );
      }
    }
  }, [availableDrivers, formVehicle]);

  const pageCount = Math.ceil(filteredVehicles.length / rowsPerPage);
  const startIndex = page * rowsPerPage;
  const pageRows = filteredVehicles.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const requestSort = (key: keyof Vehicle) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev && prev.key === key && prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const handleNewClick = () => {
    const newVehicle: Vehicle = {
      vehicleNumber: "",
      vin: "",
      model: "기아 봉고III EV",
      depot: "",
      status: "대기",
      purchaseDate: new Date().toISOString().split("T")[0],
      mileage: 0,
      obdSerialNumber: "",
      obdStatus: "-",
      id: "",
      soh: 0,
      batteryScore: 0,
      delta7d: 0,
      lastSeen: new Date().toISOString(),
      fleetOpsStatus: "Resolved",
      series30d: [],
      scoreRadar: [],
    };
    setSelectedVehicle(null);
    setFormVehicle(newVehicle);
    setIsNew(true);
    setIsEditing(true);
  };


  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(pageRows.map((v) => v.vin));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (vin: string) => {
    setSelectedIds((prev) =>
      prev.includes(vin) ? prev.filter((id) => id !== vin) : [...prev, vin]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`선택한 ${selectedIds.length}대의 차량을 삭제하시겠습니까?`)) {
      try {
        for (const vin of selectedIds) {
          await db.deleteVehicle(vin);
        }
        const updatedVehicles = await db.fetchVehicles();
        setVehicles(updatedVehicles);
        setSelectedIds([]);
        alert("삭제되었습니다.");
      } catch (error: any) {
        alert(`삭제 실패: ${error.message}`);
      }
    }
  };

  const handleSave = async () => {
    if (!formVehicle) return;
    const trimmedVehicleNumber = formVehicle.vehicleNumber.trim();
    const trimmedVin = formVehicle.vin.trim();

    if (!trimmedVehicleNumber) {
      alert("차량번호를 입력해주세요.");
      return;
    }
    if (!trimmedVin) {
      alert("VIN을 입력해주세요.");
      return;
    }
    if (!formVehicle.depot) {
      alert("지점을 선택해주세요.");
      return;
    }

    setIsSaving(true);
    try {
      const vehicleToSave: Vehicle = {
        ...formVehicle,
        vehicleNumber: trimmedVehicleNumber,
        vin: trimmedVin,
        id: trimmedVehicleNumber,
        assignee: formVehicle.assignee || undefined,
      };

      if (isNew) {
        await db.addVehicle(vehicleToSave);
      } else {
        await db.updateVehicle(vehicleToSave);
      }

      const updatedVehicles = await db.fetchVehicles();
      setVehicles(updatedVehicles);
      alert("저장되었습니다.");
      setSelectedVehicle(vehicleToSave);
      setIsEditing(false);
      setIsNew(false);
    } catch (error: any) {
      alert(`저장 실패: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;
    if (
      window.confirm(`${selectedVehicle.vehicleNumber} 차량을 삭제하시겠습니까?`)
    ) {
      try {
        await db.deleteVehicle(selectedVehicle.vin);
        const updatedVehicles = await db.fetchVehicles();
        setVehicles(updatedVehicles);
        setSelectedVehicle(null);
        setFormVehicle(null);
        setIsEditing(false);
        setIsNew(false);
        alert("삭제되었습니다.");
      } catch (error: any) {
        alert(`삭제 실패: ${error.message}`);
      }
    }
  };

  const handleCancel = () => {
    if (isNew) {
      setFormVehicle(null);
      setSelectedVehicle(null);
      setIsNew(false);
    } else {
      setFormVehicle(selectedVehicle);
      setIsEditing(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedVehicle(null);
    setFormVehicle(null);
    setIsEditing(false);
    setIsNew(false);
  };

  const handleFormChange = (field: keyof Vehicle, value: string | number) => {
    if (formVehicle) {
      setFormVehicle({ ...formVehicle, [field]: value });
    }
  };

  const handleCheckComm = () => {
    if (!formVehicle) return;
    setIsCheckingComm(true);
    setTimeout(() => {
      const validSerials = ["1234", "0000", "8207", "0120", "0201"];
      const isSuccess = validSerials.includes(formVehicle.obdSerialNumber || "");
      const newStatus: ObdStatus = isSuccess ? "통신중" : "에러";

      let batteryData = {};
      if (isSuccess) {
        const soh = 80 + Math.random() * 20;
        batteryData = {
          soh,
          batteryScore: Math.max(20, Math.min(100, Math.round(30 + ((soh - 80) / 20) * 60 + (Math.random() - 0.2) * 20))),
          delta7d: Math.round(((Math.random() - 0.5) * 5) * 10) / 10,
          lastSeen: new Date().toISOString(),
          fleetOpsStatus: ["Open", "In progress", "Resolved"][Math.floor(Math.random() * 3)] as any,
          series30d: Array.from({ length: 30 }, () => Math.max(80, Math.min(100, soh + (Math.random() - 0.5) * 4))),
          scoreRadar: Array.from({ length: 5 }, () => Math.max(20, Math.min(100, Math.round(70 + (Math.random() - 0.5) * 30)))),
        }
      }

      setFormVehicle((prev) =>
        prev ? { ...prev, obdStatus: newStatus, ...batteryData } : null
      );
      setIsCheckingComm(false);
      alert(
        isSuccess
          ? "통신 성공!"
          : "통신 실패: OBD 시리얼 번호를 확인해주세요."
      );
    }, 2000);
  };

  const isDrawerOpen = Boolean(selectedVehicle || isNew);

  return (
    <>
    <div className="vm-page">
      <div className="soh-header-left" style={{ marginBottom: '24px' }}>
        <h2 className="soh-header-title">차량 관리</h2>
        <div className="soh-header-sub">보유 차량의 등록, 수정 및 OBD 통신 상태를 관리합니다.</div>
      </div>
      {/* Toolbar */}
      <div className="driving-toolbar" style={{ marginTop: '0', border: '1px solid #e2e8f0', borderBottom: 'none', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
        <div className="driving-toolbar-left" style={{ gap: '16px' }}>
          <div className="driving-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
            차량 정보
          </div>
        </div>

        <div className="driving-toolbar-right">
          <button className="driving-action-btn primary-outline" onClick={() => window.location.reload()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            새로고침
          </button>

          <button className="driving-action-btn green-outline" onClick={() => alert("데이터 내보내기 기능은 추후 연동 예정입니다.")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="8" y1="13" x2="16" y2="13"></line>
              <line x1="8" y1="17" x2="16" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            데이터 내보내기
          </button>



          <button
            className="driving-action-btn red-outline"
            disabled={selectedIds.length === 0}
            onClick={handleBulkDelete}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            삭제
          </button>

          <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 4px' }}></div>

          <button className="driving-action-btn blue-solid" onClick={handleNewClick}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
              </svg>
              차량 등록
          </button>
          <div className="driving-search-wrapper">
              <input
                  type="text"
                  placeholder="차량번호 검색"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="driving-search-input"
              />
              <svg className="driving-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
          </div>
        </div>
      </div>

      {/* 메인 레이아웃 */}
      <div className="vm-main">
        <div className="soh-table-card">
          <div className="soh-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="soh-table premium-grid driving-grid" style={{ minWidth: '950px' }}>
              <thead>
                <tr>
                  <th className="w-chk">
                    <input
                      type="checkbox"
                      className="grid-chk"
                      checked={
                        pageRows.length > 0 &&
                        selectedIds.length === pageRows.length &&
                        pageRows.every((r) => selectedIds.includes(r.vin))
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th onClick={() => requestSort("vehicleNumber")}>차량 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("purchaseDate")}>등록일자 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("vin")}>차대번호 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("mileage")} style={{ width: '220px' }}>누적 운행거리 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("obdStatus")}>OBD <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("assignee")}>운전자 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("status")}>상태 <span className="sort-icon">▼</span></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((vehicle) => (
                  <tr
                    key={vehicle.vin}
                    className={
                      selectedVehicle?.vin === vehicle.vin ? "soh-row-selected" : ""
                    }
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setIsEditing(true); // Always edit mode
                    }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="grid-chk"
                        checked={selectedIds.includes(vehicle.vin)}
                        onChange={() => handleSelectOne(vehicle.vin)}
                      />
                    </td>
                    <td className="text-left">
                      <div className="grid-icon-text">

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>{vehicle.vehicleNumber}</span>
                            {vehicle.depot && (
                              <span style={{ fontSize: '11px', color: '#0284c7', backgroundColor: '#e0f2fe', padding: '1px 4px', borderRadius: '3px', fontWeight: 700 }}>{vehicle.depot}</span>
                            )}
                          </div>
                          <span style={{ color: '#64748b', fontSize: '13px' }}>{vehicle.model}</span>
                        </div>
                      </div>
                    </td>
                    <td className="grid-text">{vehicle.purchaseDate}</td>
                    <td className="grid-text">{vehicle.vin}</td>
                    <td>
                      <div className="mileage-bar-container">
                        <div className="mileage-track">
                          <div
                            className="mileage-fill"
                            style={{
                              width: `${Math.min(100, (vehicle.mileage / 200000) * 100)}%`,
                            }}
                          ></div>
                        </div>
                        <span className="mileage-text">
                          {vehicle.mileage.toLocaleString()} km
                        </span>
                      </div>
                    </td>
                    <td>
                      {vehicle.obdStatus === "통신중" ? (
                        <div className="grid-badge grid-badge-success">
                          정상 (연결됨)
                        </div>
                      ) : vehicle.obdStatus === "에러" ? (
                        <div className="grid-badge grid-badge-error">
                          통신 오류
                        </div>
                      ) : (
                        <div className="grid-badge grid-badge-neutral">
                          미입력
                        </div>
                      )}
                    </td>
                    <td>
                      {vehicle.assignee ? (
                        <div className="grid-icon-text">
                          <div className="grid-avatar gray" style={{ width: '24px', height: '24px' }}>{vehicle.assignee.charAt(0)}</div>
                          <span>{vehicle.assignee}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>미지정</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`grid-status-pill ${vehicle.status === "운행중"
                          ? "pill-green"
                          : vehicle.status === "대기"
                            ? "pill-blue"
                            : vehicle.status === "수리 입고"
                              ? "pill-yellow"
                              : "pill-gray"
                          }`}
                      >
                        {vehicle.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="soh-empty">
                      조건에 맞는 차량이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 (Advanced Pagination 적용) */}
          <div className="soh-pagination-advanced">
            <div className="pagination-controls">
              <button className="pag-btn pag-nav" disabled={page === 0} onClick={() => setPage(0)}>|&lt;</button>
              <button className="pag-btn pag-nav" disabled={page === 0} onClick={() => setPage(p => p - 1)}>&lt;</button>

              {Array.from({ length: Math.min(10, pageCount) }, (_, i) => {
                let startPage = Math.max(0, Math.min(page - 4, pageCount - 10));
                let pageNum = startPage + i;
                if (pageNum >= pageCount) return null;
                return (
                  <button
                    key={pageNum}
                    className={`pag-btn ${page === pageNum ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum + 1}
                  </button>
                 );
              })}

              <button className="pag-btn pag-nav" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>&gt;</button>
              <button className="pag-btn pag-nav" disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)}>&gt;|</button>
            </div>
            <div className="pagination-info">
              <span>{page + 1}/{pageCount} 페이지({filteredVehicles.length} 개)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>페이지 당 행 수:</span>
                <select className="soh-select" style={{ padding: '4px 8px' }} value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'left', fontSize: '12px', color: '#64748b', marginTop: '12px', marginLeft: '4px' }}>
            * 누적 운행거리의 100% 도달 기준은 200,000km 입니다.
          </div>
        </div>
      </div>

        {/* 우측 폼 패널 오버레이 (Drawer) */}
        <div className={`vm-backdrop ${isDrawerOpen ? "open" : ""}`} onClick={handleCloseDrawer}></div>
        <aside className={`vm-form-container ${isDrawerOpen ? "open" : ""}`}>
          {formVehicle ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <h3 className="vm-form-title" style={{ margin: 0 }}>
                  {isNew
                    ? "신규 차량 등록"
                    : isEditing
                      ? "차량 정보 수정"
                      : "차량 정보 조회"}
                </h3>
                <button
                  onClick={handleCloseDrawer}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
                >
                  &times;
                </button>
              </div>

              <div className="vm-form-field">
                <label>차량번호<span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={formVehicle.vehicleNumber}
                  onChange={(e) =>
                    handleFormChange("vehicleNumber", e.target.value)
                  }
                  placeholder="예: 12가 1111"
                  readOnly={!isNew}
                  style={!isNew ? { backgroundColor: '#f8fafc', color: '#94a3b8' } : {}}
                />
              </div>

              <div className="vm-form-field">
                <label>VIN<span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={formVehicle.vin}
                  onChange={(e) =>
                    handleFormChange("vin", e.target.value)
                  }
                  placeholder="차대번호 입력"
                  readOnly={!isNew}
                  style={!isNew ? { backgroundColor: '#f8fafc', color: '#94a3b8' } : {}}
                />
              </div>

              <div className="vm-form-field">
                <label>차종</label>
                <select
                  value={formVehicle.model}
                  onChange={(e) =>
                    handleFormChange("model", e.target.value)
                  }
                >
                  {vehicleModels.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="vm-form-field">
                <label>구매일자</label>
                <input
                  type="date"
                  value={formVehicle.purchaseDate}
                  onChange={(e) =>
                    handleFormChange("purchaseDate", e.target.value)
                  }
                />
              </div>

              <div className="vm-form-field">
                <label>누적 운행 거리</label>
                <input
                  type="text"
                  value={`${formVehicle.mileage.toLocaleString()} km`}
                  readOnly
                  style={{ backgroundColor: '#f8fafc', color: '#94a3b8' }}
                />
              </div>

              <div className="vm-form-field">
                <label>OBD 시리얼 번호</label>
                <div className="vm-form-field-group">
                  <input
                    type="text"
                    value={formVehicle.obdSerialNumber || ""}
                    onChange={(e) =>
                      handleFormChange("obdSerialNumber", e.target.value)
                    }
                    placeholder="시리얼 번호 입력"
                  />
                  <button
                    className="vm-btn"
                    onClick={handleCheckComm}
                    disabled={isCheckingComm}
                  >
                    {isCheckingComm ? "확인중..." : "통신확인"}
                  </button>
                </div>
              </div>

              <div className="vm-form-field">
                <label>OBD 통신 상태</label>
                <input
                  type="text"
                  className={`vm-status-display ${formVehicle.obdStatus === "통신중"
                    ? "comm-ok"
                    : formVehicle.obdStatus === "에러"
                      ? "comm-error"
                      : ""
                    }`}
                  value={formVehicle.obdStatus}
                  readOnly
                  style={{ backgroundColor: '#f8fafc', color: '#94a3b8' }}
                />
              </div>

              <div className="vm-form-field">
                <label>지점</label>
                <select
                  value={formVehicle.depot}
                  onChange={(e) =>
                    handleFormChange("depot", e.target.value)
                  }
                >
                  <option value="" disabled>
                    지점 선택
                  </option>
                  {depots.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              <div className="vm-form-field">
                <label>운전자</label>
                <select
                  value={formVehicle.assignee || ""}
                  onChange={(e) =>
                    handleFormChange("assignee", e.target.value)
                  }
                  disabled={!formVehicle.depot}
                >
                  <option value="">미지정</option>
                  {availableDrivers.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="vm-form-field">
                <label>상태</label>
                <select
                  value={formVehicle.status}
                  onChange={(e) =>
                    handleFormChange("status", e.target.value)
                  }
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="vm-drawer-footer">
                <div className="drawer-footer-left">
                  {!isNew && (
                    <button className="drawer-btn-delete" onClick={handleDelete}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                      삭제
                    </button>
                  )}
                </div>
                <div className="drawer-footer-right">
                  <button className="drawer-btn-cancel" onClick={handleCancel}>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    취소
                  </button>
                  <button
                    className="drawer-btn-save"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {isSaving ? "저장중..." : "저장하기"}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </aside>
      </div>

      <PriceCheckModal
        isOpen={isPriceModalOpen}
        onClose={() => setIsPriceModalOpen(false)}
        vehicles={vehicles.filter((v) => selectedIds.includes(v.vin))}
      />
    </>
  );
}
